import { __awaiter, __decorate, __rest } from "tslib";
import { Connect } from '@genesislcap/foundation-comms';
import { renderers } from '@genesislcap/foundation-forms';
import { showNotification, showNotificationDialog } from '@genesislcap/foundation-notifications';
import { LifecycleMixin } from '@genesislcap/foundation-utils';
import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { getErrorFormat, logger } from '../utils';
import { styles } from './entities.styles';
import { template } from './entities.template';
/**
 * Main class which defines the entity management functionality
 *
 * @remarks
 *
 * Connects to a backend resource and wraps up a grid which is populated
 * with entities from that resource. The different interactions that the user
 * can perform with the entities can be configured, examples being able to
 * update and delete entities.
 *
 * @public
 *
 * @example
 * Example of using the entity management system to handle counterparties
 * ```
 * <entity-management
 *   resourceName="ALL_COUNTERPARTYS"
 *   title="Counterparty Management"
 *   updateEvent="EVENT_COUNTERPARTY_MODIFY"
 *   deleteEvent="EVENT_COUNTERPARTY_DELETE"
 *   createEvent="EVENT_COUNTERPARTY_INSERT"
 * ></entity-management>
 * ```
 * Where:<br />
 * - the title of the grid is `Counterparty Management`<br />
 * - the name of the resource in the database to manage is `ALL_COUNTERPARTYS`<br />
 * - the name of the event handler for update events is `EVENT_COUNTERPARTY_MODIFY`<br />
 * - the name of the event handler for create events is `EVENT_COUNTERPARTY_INSERT`<br />
 * - the name of the event handler for delete events is `EVENT_COUNTERPARTY_DELETE`<br />
 *
 */
let EntityManagement = class EntityManagement extends LifecycleMixin(FASTElement) {
    constructor() {
        super(...arguments);
        /**
         * Label for the entity which has usages such as being shown in the title of the modal wen editing the entity
         * @public
         */
        this.entityLabel = '';
        /**
         * Whether to use the `applyTransactionAsync` function for *add* transactions
         * @remarks Defaults to false
         * @public
         */
        this.asyncAdd = false;
        /**
         * Whether to use the `applyTransactionAsync` function for *remove* transactions
         * @remarks Defaults to false
         * @public
         */
        this.asyncRemove = false;
        /**
         * Whether to use the `applyTransactionAsync` function for *update* transactions
         * @remarks Defaults to true
         * @public
         */
        this.asyncUpdate = true;
        /**
         * If false, will disable cell flashing for all cells by default, unless otherwise defined in custom colDef
         * @remarks Defaults to true
         * @public
         */
        this.enableCellFlashing = false;
        /**
         * If true, will enable row flashing for all rows for `add` transactions
         * @public
         */
        this.enableRowFlashing = false;
        /**
         * Array with renderers used by foundation-forms
         * @public
         */
        this.formRenderers = renderers;
        this.editModalVisible = false;
        this.hideEdit = false;
        this.hideDelete = false;
        /**
         * Determines where the modal dialog will appear on screen
         * @public
         */
        this.modalPosition = 'right';
        /**
         * Updates the reference to the current entity stored in the class. Added as an event listener on the class when receiving the `rowSelected` event.
         *
         * @param e - CustomEvent which contains data about the selected entity. If the row is not selected then the reference will be set to an empty object.
         * @internal
         */
        this.selectEntity = (e) => {
            this.selectedEntity = e.detail.node.isSelected() ? e.detail.data : {};
        };
    }
    editModalVisibleChanged() {
        if (this.editEntityModal) {
            if (this.editModalVisible) {
                this.editEntityModal.show();
            }
            else {
                this.editEntityModal.close();
            }
        }
    }
    /**
     * Set up the web component
     * @internal
     */
    connectedCallback() {
        const _super = Object.create(null, {
            connectedCallback: { get: () => super.connectedCallback }
        });
        return __awaiter(this, void 0, void 0, function* () {
            _super.connectedCallback.call(this);
            this.addEventListener('rowSelected', this.selectEntity);
            if (this.deleteEvent) {
                this.addEventListener('delete-entity', this.deleteEntity);
            }
            this.addEventListener('create-entity', this.createEntity);
            this.addEventListener('edit-entity', this.editEntity);
            this.addEventListener('read-entity', this.readEntity);
            this.addEventListener('criteria-changed', this.criteriaChanged);
            this.addEventListener('submit-failure', this.submitFailureNotification);
        });
    }
    /**
     * Override the deepClone method to ensure that observable attributes are cloned
     * @public
     */
    deepClone() {
        const copy = super.deepClone();
        copy.createFormUiSchema = structuredClone(this.createFormUiSchema);
        copy.datasourceConfig = structuredClone(this.datasourceConfig);
        copy.editDialogTitle = structuredClone(this.editDialogTitle);
        copy.editModalVisible = structuredClone(this.editModalVisible);
        copy.formResourceName = structuredClone(this.formResourceName);
        copy.formUiSchema = structuredClone(this.formUiSchema);
        copy.updateFormUiSchema = structuredClone(this.updateFormUiSchema);
        copy.defaultEntityValues = structuredClone(this.defaultEntityValues);
        // this.columns[number] and this.formRenderers[number] might contain a function, so can't use structuredClone
        copy.columns = this.columns ? [...this.columns].map((x) => (Object.assign({}, x))) : this.columns;
        copy.formRenderers = this.formRenderers
            ? [...this.formRenderers].map((x) => (Object.assign({}, x)))
            : this.formRenderers;
        return copy;
    }
    /**
     * Event handler for when the user submits the action for the currently open form, either editing or adding the entity
     *
     * @param e - CustomEvent which contains the payload for the entity to submit to the backend
     *
     * Emits an event upon error
     * @public
     */
    submitEntityChanges(e) {
        return __awaiter(this, void 0, void 0, function* () {
            this.closeModal();
        });
    }
    /**
     * Show the entity create form
     * Sets the initial value on the form to be defaultEntityValues if present.
     * @internal
     */
    createEntity() {
        this.editDialogTitle = `Add ${this.entityLabel}`;
        this.formUiSchema = this.createFormUiSchema;
        this.formResourceName = this.createEvent;
        this.editedEntity = Object.assign({}, this.defaultEntityValues);
        this.editModalVisible = true;
    }
    /**
     * Opens the model to edit an entity. Added as an event listener on the class when receiving the `edit-entity` event.
     *
     * @param e - CustomEvent where `e.detail` is the entity to edit.
     * @internal
     */
    editEntity({ detail }) {
        return __awaiter(this, void 0, void 0, function* () {
            this.editDialogTitle = `Edit ${this.entityLabel}`;
            this.formUiSchema = this.updateFormUiSchema;
            this.formResourceName = this.updateEvent;
            if (detail) {
                const { ROW_REF } = detail, formData = __rest(detail, ["ROW_REF"]);
                this.editedEntity = formData;
                if (this.readEventFn) {
                    const readEntityReq = yield this.readEventFn(formData);
                    this.editedEntity = Object.assign(Object.assign({}, this.editedEntity), readEntityReq);
                }
                else if (this.readEvent) {
                    const readEventReq = yield this.connect.request(this.readEvent, {
                        REQUEST: formData,
                    });
                    this.editedEntity = Object.assign(Object.assign({}, this.editedEntity), readEventReq.REPLY[0]);
                }
            }
            else {
                this.editedEntity = Object.assign({}, this.defaultEntityValues);
            }
            this.editModalVisible = true;
        });
    }
    readEntity(e) {
        this.readonly = true;
        this.editEntity(e);
        this.editDialogTitle = '';
    }
    closeModal() {
        this.readonly = false;
        this.editModalVisible = false;
    }
    criteriaChanged(e) {
        this.datasourceConfig = Object.assign(Object.assign({}, this.datasourceConfig), { criteria: e.detail });
    }
    submitFailureNotification(e) {
        e.detail.errors.forEach((submitFailureError) => {
            if (submitFailureError.CODE === 'OPTIMISTIC_CONCURRENCY_ERROR') {
                showNotification({
                    title: 'Warning',
                    body: 'You’re editing an old revision. Please close the form and try editing again',
                    config: {
                        snackbar: {
                            type: 'error',
                        },
                    },
                }, 'zero');
            }
            else {
                showNotification({
                    title: 'Error submitting form',
                    body: (submitFailureError.CODE + ': ' + submitFailureError.TEXT).toString(),
                    config: {
                        snackbar: {
                            type: 'error',
                            confirmingActions: [{ label: '', action: () => { } }],
                        },
                    },
                }, 'zero');
            }
        });
    }
    /**
     * Handler for deleting the entity. Added as an event listener on the class when receiving the `delete-entity` event.
     *
     * @param e - CustomEvent where `e.detail` contains data to update the selectedEntity reference with the entity to delete.
     * @internal
     */
    deleteEntity(e) {
        return __awaiter(this, void 0, void 0, function* () {
            this.selectedEntity = e.detail;
            this.showDeleteConfirmation();
        });
    }
    confirmDelete() {
        return __awaiter(this, void 0, void 0, function* () {
            const schema = yield this.connect.getJSONSchema(this.deleteEvent);
            const DETAILS = {};
            if (typeof schema.INBOUND.properties.DETAILS !== 'boolean') {
                const requiredFields = Object.keys(schema.INBOUND.properties.DETAILS.properties);
                if (requiredFields) {
                    requiredFields.forEach((property) => {
                        DETAILS[property] = this.selectedEntity[property];
                    });
                    const deleteReq = yield this.connect.commitEvent(this.deleteEvent, {
                        DETAILS,
                    });
                    this.errorNotify(deleteReq);
                }
                else {
                    logger.error('No required fields found for delete event', this.deleteEvent);
                }
            }
        });
    }
    showDeleteConfirmation() {
        showNotificationDialog({
            title: 'Confirm Delete',
            body: 'Do you really want to delete this entity?',
            dialog: {
                dismissingAction: { label: 'Dismiss', action: () => (this.selectedEntity = null) },
                confirmingActions: [{ label: 'Confirm', action: () => this.confirmDelete() }],
            },
        }, 'zero');
    }
    /**
     * Show notifications if the executed event returned an error.
     * @internal
     */
    errorNotify(request) {
        const notificationCloseTimeout = 5000;
        if (request.ERROR) {
            logger.error(request.ERROR);
            request.ERROR.forEach((err) => {
                showNotification({
                    title: err.CODE,
                    body: getErrorFormat(err),
                    config: {
                        toast: { type: 'warning', autoClose: true, closeTimeout: notificationCloseTimeout },
                    },
                }, 'zero');
            });
        }
    }
};
__decorate([
    Connect
], EntityManagement.prototype, "connect", void 0);
__decorate([
    attr
], EntityManagement.prototype, "resourceName", void 0);
__decorate([
    attr
], EntityManagement.prototype, "readEvent", void 0);
__decorate([
    observable
], EntityManagement.prototype, "readEventFn", void 0);
__decorate([
    attr({ mode: 'boolean' })
], EntityManagement.prototype, "readonly", void 0);
__decorate([
    attr
], EntityManagement.prototype, "updateEvent", void 0);
__decorate([
    attr
], EntityManagement.prototype, "deleteEvent", void 0);
__decorate([
    attr
], EntityManagement.prototype, "createEvent", void 0);
__decorate([
    attr
], EntityManagement.prototype, "title", void 0);
__decorate([
    attr
], EntityManagement.prototype, "entityLabel", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'async-add' })
], EntityManagement.prototype, "asyncAdd", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'async-remove' })
], EntityManagement.prototype, "asyncRemove", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'async-update' })
], EntityManagement.prototype, "asyncUpdate", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'enable-cell-flashing' })
], EntityManagement.prototype, "enableCellFlashing", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'enable-row-flashing' })
], EntityManagement.prototype, "enableRowFlashing", void 0);
__decorate([
    attr({ attribute: 'persist-column-state-key' })
], EntityManagement.prototype, "persistColumnStateKey", void 0);
__decorate([
    observable
], EntityManagement.prototype, "gridOptions", void 0);
__decorate([
    observable
], EntityManagement.prototype, "columns", void 0);
__decorate([
    observable
], EntityManagement.prototype, "datasourceConfig", void 0);
__decorate([
    observable
], EntityManagement.prototype, "formUiSchema", void 0);
__decorate([
    observable
], EntityManagement.prototype, "createFormUiSchema", void 0);
__decorate([
    observable
], EntityManagement.prototype, "updateFormUiSchema", void 0);
__decorate([
    observable
], EntityManagement.prototype, "formResourceName", void 0);
__decorate([
    observable
], EntityManagement.prototype, "formRenderers", void 0);
__decorate([
    observable
], EntityManagement.prototype, "selectedEntity", void 0);
__decorate([
    observable
], EntityManagement.prototype, "editedEntity", void 0);
__decorate([
    observable
], EntityManagement.prototype, "submitting", void 0);
__decorate([
    observable
], EntityManagement.prototype, "defaultEntityValues", void 0);
__decorate([
    observable
], EntityManagement.prototype, "editDialogTitle", void 0);
__decorate([
    observable
], EntityManagement.prototype, "editModalVisible", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'size-columns-to-fit' })
], EntityManagement.prototype, "sizeColumnsToFit", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'enable-filter-bar' })
], EntityManagement.prototype, "enableFilterBar", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'hide-edit' })
], EntityManagement.prototype, "hideEdit", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'hide-delete' })
], EntityManagement.prototype, "hideDelete", void 0);
__decorate([
    attr({ attribute: 'modal-position' })
], EntityManagement.prototype, "modalPosition", void 0);
EntityManagement = __decorate([
    customElement({
        name: 'entity-management',
        template,
        styles,
    })
], EntityManagement);
export { EntityManagement };
